<template>
  <div class="documents-library-container">
    <b-alert variant="danger" :show="noPermissions">
      {{ $t("integrations.media.noPermissions") }}
    </b-alert>

    <!-- Empty State -->
    <div v-if="$isAdmin() && libraries && libraries.length < 1" class="empty-library-state">
      <div class="empty-icon">
        <b-icon icon="file-earmark-text" font-scale="3"></b-icon>
      </div>
      <h4>{{ $t("integrations.media.startByCreating") }}</h4>
      <b-button @click="createLibrary.show = true" variant="primary" size="lg" class="mt-3">
        <b-icon icon="plus-circle" class="mr-2"></b-icon>
        {{ $t('add') }}
      </b-button>
    </div>

    <div 
      v-if="libraries && currentLib" 
      class="documents-library-content"
      @dragenter.prevent="handleGlobalDragEnter"
      @dragleave.prevent="handleGlobalDragLeave"
      @dragover.prevent="handleGlobalDragOver"
      @drop.prevent="handleGlobalDrop"
    >
      <!-- Drag and Drop Overlay -->
      <div 
        v-if="isDragging"
        class="drop-overlay"
      >
        <div class="drop-overlay-content">
          <b-icon icon="cloud-upload" font-scale="4" class="drop-icon"></b-icon>
          <p class="drop-text">{{ $t('dragAndDropFiles') }}</p>
        </div>
      </div>

      <!-- Modern Header -->
      <div class="documents-header">
        <div class="header-top">
          <h3 class="library-title">
            <b-icon icon="file-earmark-text" class="mr-2"></b-icon>
            {{ $t('documents') }}
          </h3>
          <div class="header-actions">
            <b-button 
              variant="outline-secondary" 
              size="sm" 
              @click="$router.push('/integrations/page/protokol-media/media')"
              class="mr-2"
            >
              <b-icon icon="folder2-open" class="mr-1"></b-icon>
              {{ $t('mediaLibrary') }}
            </b-button>
            <b-button-group size="sm" class="view-toggle mr-3">
              <b-button :variant="viewMode === 'grid' ? 'primary' : 'outline-secondary'" @click="viewMode = 'grid'">
                <b-icon icon="grid-3x3-gap"></b-icon>
              </b-button>
              <b-button :variant="viewMode === 'list' ? 'primary' : 'outline-secondary'" @click="viewMode = 'list'">
                <b-icon icon="list-ul"></b-icon>
              </b-button>
            </b-button-group>
            <b-button variant="outline-primary" size="sm" @click="uploadModal.show = true">
              <b-icon icon="file-earmark-plus" class="mr-1"></b-icon>
              New document
            </b-button>
          </div>
        </div>

        <!-- Search and Breadcrumb -->
        <div class="header-search-breadcrumb">
          <div class="breadcrumb-nav">
            <b-button variant="link" @click="chdir('')" class="breadcrumb-item">
              <b-icon icon="house-fill"></b-icon>
            </b-button>
            <template v-for="(dir, index) in dirPath">
              <span v-if="dir" :key="'sep-' + index" class="breadcrumb-separator">/</span>
              <b-button 
                v-if="dir"
                :key="dir" 
                variant="link" 
                @click="chdir(dirPath.slice(0, index + 1).join('/'))"
                :class="['breadcrumb-item', { 'active': index === dirPath.length - 1 }]"
              >
                {{ dir }}
              </b-button>
            </template>
          </div>
          <div class="search-box">
            <b-icon icon="search" class="search-icon"></b-icon>
            <b-form-input
              :placeholder="`${$t('search')}...`"
              v-model="keyword"
              @input="search"
              class="search-input"
            />
          </div>
        </div>
      </div>

      <b-spinner v-if="changingLib" class="loading-spinner" />

      <!-- Toolbar -->
      <div v-else class="documents-toolbar">
        <div class="toolbar-left">
          <span v-if="itemsSelected.length > 0" class="selection-count">
            {{ itemsSelected.length }} {{ $t('selected') }}
          </span>
        </div>
        <div class="toolbar-right">
          <template v-if="itemsSelected.length > 0 && itemsSelected.length == 1">
            <b-button 
              variant="outline-primary" 
              size="sm" 
              @click="editFile('generate')"
              class="mr-2"
            >
              <b-icon icon="lightning-charge"></b-icon>
              {{ $t("generate") }}
            </b-button>
            <b-button 
              variant="outline-success" 
              size="sm" 
              @click="editFile()"
              class="mr-2"
            >
              <b-icon icon="pencil"></b-icon>
              {{ $t("edit") }}
            </b-button>
            <b-button 
              variant="outline-danger" 
              size="sm" 
              @click="deleteFile"
            >
              <b-icon icon="trash"></b-icon>
              {{ $t("delete") }}
            </b-button>
          </template>
        </div>
      </div>

      <!-- Directories Grid -->
      <div v-if="dirs && dirs.size > 0" class="directories-section">
        <div :class="['directories-grid', `view-${viewMode}`]">
          <div 
            v-for="dirPath in dirs" 
            :key="dirPath" 
            class="directory-card"
            @click="chdir(dirPath)"
          >
            <div class="directory-icon">
              <b-icon icon="folder-fill" font-scale="2"></b-icon>
            </div>
            <div class="directory-info">
              <span class="directory-name">
                {{ dirPath.replace(currentDirRegex, "").replace("/", "") }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents Grid/List -->
      <div v-if="items !== undefined" :class="['documents-section', `view-${viewMode}`]">
        <DocumentIcon
          v-for="item in itemsInCurDir"
          :key="item.name"
          :doc="item"
          :library="currentLib"
          :dir="currentDir"
          :itemsSelected="itemsSelected"
          :viewMode="viewMode"
          @selectFile="(e) => selectFile(e, item)"
          @showContextMenu="handleShowContextMenu"
          @contextAction="handleContextAction"
          :ref="`doc-${item.name}`"
        />
      </div>

      <NewDocumentModal
        :uploadModal="uploadModal"
        :currentLib="currentLib"
        :currentDir="currentDir"
      />
    </div>

    <b-skeleton v-else width="100%" height="250px" animation="throb" />

    <b-sidebar v-model="createLibrary.show" right backdrop>
      <div class="px-3 py-2">
        <InputField
          name="name"
          :label="this.$t('name')"
          v-model="createLibrary.data.name"
          @error="fieldError"
          :rules="{ required: true, length: { min: 2 } }"
        />

        <template v-if="!fieldHasError('name')">
          <SelectField
            name="roles"
            :label="this.$t('roles')"
            :description="this.$t('allowedRoles')"
            v-model="createLibrary.data.roles"
            :items="roles"
            :rules="{ required: false }"
            :multiple="true"
          />

          <SelectField
            name="provider"
            :label="this.$t('provider')"
            :description="this.$t('chooseMediaProvider')"
            v-model="createLibrary.data.provider"
            :items="libProviders"
            :reduce="(o) => o"
            :rules="{ required: true }"
          />
        </template>

        <!-- <div v-if="createLibrary.data.provider == 'protokol-simple'">
                {{  $t('integrations.media.protokolSimpleDescription')  }}
            </div> -->

        <template v-if="createLibrary.data.provider == 's3'">
          <InputField
            name="endpoint"
            :label="this.$t('endpoint')"
            v-model="createLibrary.data.s3_endpoint"
            @error="fieldError"
            :rules="{ required: true, length: { min: 2 } }"
          />

          <InputField
            name="region"
            :label="this.$t('region')"
            v-model="createLibrary.data.s3_region"
            @error="fieldError"
            :rules="{ required: true, length: { min: 2 } }"
          />

          <InputField
            name="bucket"
            :label="this.$t('bucket')"
            v-model="createLibrary.data.s3_bucket"
            @error="fieldError"
            :rules="{ required: true, length: { min: 2 } }"
          />

          <InputField
            name="key"
            :label="this.$t('key')"
            v-model="createLibrary.data.s3_key"
            @error="fieldError"
            :rules="{ required: true, length: { min: 2 } }"
          />

          <InputField
            type="password"
            name="secret"
            :label="this.$t('secret')"
            v-model="createLibrary.data.s3_secret"
            @error="fieldError"
            :rules="{ required: true, length: { min: 2 } }"
          />

          <InputField
            name="host"
            :label="this.$t('host')"
            v-model="createLibrary.data.host"
            @error="fieldError"
            :description="this.$t('libraryPublicUrl')"
            :rules="{
              required: true,
              placeholder: 'https://library.example.com',
              length: { min: 2 },
            }"
          />

          <InputField
            name="dir"
            :label="this.$t('rootDirMedia')"
            v-model="createLibrary.data.dir"
            @error="fieldError"
            :description="this.$t('rootDirMedia')"
            :rules="{ required: false, length: { min: 2 } }"
          />
        </template>
      </div>
      <template #footer>
        <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
          <b-button
            size="sm"
            variant="success"
            @click="createLibraryAction"
            v-if="canShowCreate"
            >{{ $t("create") }}</b-button
          >
          <protokol-checkout-button
            size="sm"
            v-if="canShowBuy"
            productType="integration"
            reference="protokol-media"
            >{{ $t("buy") }}</protokol-checkout-button
          >
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.documents-library-container {
  padding: 24px;
  min-height: 100vh;
}

.empty-library-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: $dark-background;
  border: 2px dashed $dark-border2;
  border-radius: 16px;
  text-align: center;

  .empty-icon {
    color: var(--text-muted);
    margin-bottom: 24px;
    opacity: 0.5;
  }

  h4 {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 12px;
  }
}

.documents-library-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
}

.drop-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease;

  .drop-overlay-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 60px;
    background: $dark-background;
    border: 3px dashed $protokol-royal-active;
    border-radius: 20px;
    animation: scaleIn 0.3s ease;

    .drop-icon {
      color: $protokol-royal-active;
      animation: pulse 1.5s ease-in-out infinite;
    }

    .drop-text {
      color: var(--text-primary);
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0;
      text-align: center;
    }
  }
}

.documents-header {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;

  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .library-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0;
      display: flex;
      align-items: center;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 12px;

      .view-toggle {
        ::v-deep button {
          padding: 8px 12px;
        }
      }
    }
  }

  .header-search-breadcrumb {
    display: flex;
    gap: 20px;
    align-items: center;
    flex-wrap: wrap;

    .breadcrumb-nav {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 200px;
      padding: 12px 16px;
      background: $dark-hard2-background;
      border-radius: 8px;
      border: 1px solid $dark-border2;

      .breadcrumb-item {
        color: $protokol-royal-active;
        text-decoration: none;
        padding: 0 4px;
        font-weight: 500;
        transition: all 0.2s;

        &:hover {
          color: #7d92f5;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 4px;
        }

        &.active {
          color: var(--text-primary);
          pointer-events: none;
        }
      }

      .breadcrumb-separator {
        color: var(--text-muted);
        font-size: 0.9rem;
      }
    }

    .search-box {
      position: relative;
      min-width: 280px;

      .search-icon {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: var(--text-muted);
        pointer-events: none;
      }

      .search-input {
        padding-left: 40px;
        background: $dark-hard2-background;
        border: 1px solid $dark-border2;
        color: var(--text-primary);
        border-radius: 8px;
        height: 42px;

        &:focus {
          background: $dark-hard2-background;
          border-color: $protokol-royal-active;
          color: var(--text-primary);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        &::placeholder {
          color: var(--text-muted);
        }
      }
    }
  }
}

.loading-spinner {
  display: flex;
  justify-content: center;
  padding: 60px;
}

.documents-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  margin-bottom: 20px;

  .toolbar-left {
    .selection-count {
      color: var(--text-primary);
      font-weight: 600;
      padding: 8px 16px;
      background: rgba(102, 126, 234, 0.15);
      border-radius: 6px;
      font-size: 0.9rem;
    }
  }

  .toolbar-right {
    display: flex;
    gap: 8px;
  }
}

.directories-section {
  margin-bottom: 32px;
}

.directories-grid {
  display: grid;
  gap: 16px;
  
  &.view-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  &.view-list {
    grid-template-columns: 1fr;
  }

  .directory-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: $dark-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: $protokol-royal-active;
      background: rgba(102, 126, 234, 0.05);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .directory-icon {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(102, 126, 234, 0.15);
      border-radius: 10px;
      color: $protokol-royal-active;
      flex-shrink: 0;
    }

    .directory-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;

      .directory-name {
        font-weight: 600;
        color: var(--text-primary);
        font-size: 1rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}

.documents-section {
  display: grid;
  gap: 16px;

  &.view-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  &.view-list {
    grid-template-columns: 1fr;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .documents-header {
    .header-top {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
    }

    .header-search-breadcrumb {
      flex-direction: column;
      
      .breadcrumb-nav,
      .search-box {
        width: 100%;
        min-width: 100%;
      }
    }
  }

  .documents-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;

    .toolbar-left,
    .toolbar-right {
      width: 100%;
      justify-content: center;
    }

    .toolbar-right {
      display: flex;
      gap: 8px;
    }
  }

  .directories-grid {
    &.view-grid {
      grid-template-columns: 1fr;
    }
  }

  .documents-section {
    &.view-grid {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    }
  }
}
</style>

<script>
import Input from "@/components/form/Input";
import Select from "@/components/form/Select";
import Page500 from "../../../../views/pages/Page500";
import HelptipPopup from "../../../elements/HelptipPopup";
import _ from "lodash";
import File from "./File";
import DocumentIcon from "./Documents/DocumentIcon.vue";
import NewDocumentModal from "./Documents/NewDocumentModal.vue";

export default {
  name: "Documents",
  props: {
    mode: {
      type: String,
      default: "library",
    },
  },

  components: {
    InputField: Input,
    SelectField: Select,
    ErrorPage: Page500,
    File,
    HelptipPopup,
    DocumentIcon,
    NewDocumentModal,
    // UploadFileModal,
    //DateTimeField: DateTime
  },
  data: function () {
    return {
      libraryUnavailable: false,
      ready: false,
      noPermissions: false,
      changingLib: false,
      errors: [],
      dirPath: [],
      itemsSelected: [],
      keyword: "",
      viewMode: 'grid',
      isDragging: false,
      dragCounter: 0,
      uploadModal: {
        show: false,
        name: "",
      },
      dirModal: {
        show: false,
        name: "",
      },
      moveModal: {
        show: false,
        dir: "",
      },
      storage: {
        allowedSpace: 0,
        freeSpace: 0,
      },
      editLibrary: {
        show: false,
        data: null,
      },
      createLibrary: {
        show: false,
        data: {
          name: "",
          roles: [],
        },
      },
      roles: [],
      share: {
        types: ["link", "email"],
        type: "link",
        url: null,
        emails: "",
        validUntil: "",
        mimeType: "application/octet-stream",
        key: "",
      },

      lastKey: null,
      limit: 12,
    };
  },
  watch: {
    "fileModal.data.orig_name": function (nVal, oVal) {
      this.fileModal.data.orig_name = nVal.replace(" ", "_");
    },
    "dirModal.name": function (nVal, oVal) {
      this.dirModal.name = nVal.replace("/", "");
    },
  },
  computed: {
    items() {
      return this.$store.getters["documents/documents"].filter(
        (doc) => doc.name !== "/"
      ); // (this.currentDir)
    },

    dirs() {
      return new Set(
        this.items
          .filter(
            (doc) =>
              doc.name.replace(this.currentDirRegex, "").split("/").length >=
                3 && doc.name.startsWith(this.currentDir)
          )
          .map(
            (doc) =>
              `${this.currentDir}/${
                doc.name.replace(this.currentDirRegex, "").split("/")[1]
              }`
          )
      );
    },

    itemsInCurDir() {
      return this.items.filter(
        (doc) =>
          doc.name.replace(this.currentDirRegex, "").split("/").length === 2 &&
          doc.name.startsWith(this.currentDir)
      );
    },

    currentDir() {
      return this.dirPath.join("/");
    },
    currentDirRegex() {
      return new RegExp("^" + this.escapeRegExp(this.currentDir));
    },

    prevDir() {
      return this.dirPath[this.dirPath.length - 2];
    },

    dirSize() {
      if (this.mode == "library") return 3;
      return 4;
    },

    fileSize() {
      if (this.mode == "library") return 2;
      return 3;
    },

    libraries: {
      set(val) {
        this.$store.commit("SET_LIBRARIES", val);
      },

      get() {
        return this.$store.getters.libraries;
      },
    },

    currentLib: {
      set(val) {
        if (this.mode != "library") {
          this.$store.commit("documents/SET_LIBRARY_UUID", val.uuid);
          return;
        }

        this.$router.replace({
          query: {
            lib: val.uuid,
          },
        });

        this.$store.commit("documents/SET_LIBRARY_UUID", val.uuid);
      },

      get() {
        if (this.mode == "standalone") {
          return this.libraries[0];
        }

        let lib = this.libraries[0].uuid; // this.$route.query.lib;
        if (lib === undefined) {
          return this.$store.getters["documents/library"];
        }

        return this.libraries.find((l) => {
          return l.uuid == lib;
        });
      },
    },

    libProviders() {
      let providers = ["protokol-simple"];

      const libs = this.libraries || [];
      const exist = libs.find((l) => {
        return l.provider == "protokol-simple";
      });

      if (exist) {
        providers.pop();
      }

      return providers;
    },

    s3Secret: {
      get() {
        return "__secret__";
      },

      set(val) {
        this.currentLib.s3_secret = val;
      },
    },

    canShowSettings() {
      if (this.mode == "standalone") return false;

      return this.$route.path.includes("/integrations/page/protokol-media");
    },

    currentLibName() {
      if (this.currentLib) {
        if (this.currentLib.name != "") {
          return this.truncate(this.currentLib.name, { length: 15 });
        }

        return this.$t("default");
      }
    },

    canShowBuy() {
      if (this.createLibrary.data.provider == "protokol-simple") {
        let integration =
          this.$store.getters.getIntegrationByID("protokol-media");
        let subs = this.$store.getters.getSubscriptions || [];

        let sub = subs.find((s) => {
          return s.product_id == integration.template.product_id;
        });

        if (sub) {
          return false;
        }

        return true;
      }

      return false;
    },

    canShowCreate() {
      if (this.canShowBuy) return false;

      let libs = this.libraries || [];
      return (
        libs.find((l) => {
          return l.provider == "protokol-simple";
        }) === undefined
      );
    },
  },
  methods: {
    fieldHasError(field) {
      const res =
        this.errors.find((f) => {
          return f.field == field && f.error;
        }) !== undefined;

      return res;
    },

    truncate: _.truncate,

    chdir(dir) {
      this.dirPath = dir.split("/");

      this.itemsSelected = [];

      if (this.mode == "library") {
        history.pushState(null, null, "#" + this.currentDir);
      }
    },

    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },

    handleGlobalDragEnter(e) {
      if (e.dataTransfer.types.includes('Files')) {
        this.dragCounter++
        this.isDragging = true
      }
    },

    handleGlobalDragLeave(e) {
      this.dragCounter--
      if (this.dragCounter === 0) {
        this.isDragging = false
      }
    },

    handleGlobalDragOver(e) {
      if (e.dataTransfer.types.includes('Files')) {
        e.dataTransfer.dropEffect = 'copy'
      }
    },

    handleGlobalDrop(e) {
      this.isDragging = false
      this.dragCounter = 0

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        // Handle document upload
        this.uploadModal.show = true
      }
    },

    // downloadFile() {
    //   let item = this.itemsSelected[0];
    //   this.$media
    //     .download(this.currentLib.uuid, item.key)
    //     .then((resp) => {
    //       const url = window.URL.createObjectURL(new Blob([resp.data]));
    //       const link = document.createElement("a");
    //       link.href = url;
    //       link.setAttribute("download", item.name); //or any other extension
    //       document.body.appendChild(link);
    //       link.click();
    //     })
    //     .catch((err) => {
    //       console.log(err);
    //     });
    // },

    selectFile(e, item) {
      if (e.ctrlKey || e.metaKey) {
        let find = this.itemsSelected.find(({ name }) => {
          return item.name == name;
        });

        if (find) {
          this.itemsSelected = this.itemsSelected.filter(({ name }) => {
            return item.name != name;
          });
        } else {
          this.itemsSelected.push(item);
        }
      } else {
        this.itemsSelected = [item];
      }

      this.$emit("itemsSelected", this.itemsSelected);
    },

    editFile(mode) {
      const doc = this.itemsSelected[0];

      window.open(
        `/integrations/page/protokol-media/editor?lib=${
          this.currentLib.uuid
        }&document=${doc.name}${mode ? `&mode=${mode}` : ""}`,
        "_blank"
      );
    },

    handleShowContextMenu(docName) {
      // Close all other context menus
      Object.keys(this.$refs).forEach(key => {
        if (key.startsWith('doc-') && key !== `doc-${docName}`) {
          const ref = this.$refs[key];
          if (ref && ref[0] && ref[0].closeContextMenu) {
            ref[0].closeContextMenu();
          }
        }
      });
    },

    handleContextAction({ action, document }) {
      // Select the document
      this.itemsSelected = [document];
      
      switch(action) {
        case 'edit':
          this.editFile();
          break;
        case 'generate':
          this.editFile('generate');
          break;
        case 'delete':
          this.deleteFile();
          break;
      }
    },

    deleteFile() {
      const doc = this.itemsSelected[0];

      this.$mainDialog
        .msgBoxConfirm(this.$t("selectedFilesWillBeDeleted"))
        .then((resp) => {
          if (resp) {
            let popup = this.$loadingPopup(this.$t("deleting") + "...");

            this.$store
              .dispatch("documents/deleteDocument", { path: doc.name })
              .catch((error) => {
                if (error.response !== undefined) {
                  if (error.response.status == 500) {
                    this.$serverErrorModal(error);
                    return;
                  }

                  this.$mainDialog.msgBoxOk(error.response.message);
                }

                throw error;
              })
              .finally(() => {
                this.itemsSelected = [];
                popup.hide();
              });
          }
        });
    },

    search: _.debounce(function (searchText) {
      if (searchText == "") {
        this.$store
          .dispatch("documents/fetchDocuments", {
            searchText: this.currentDir,
            recursive: true,
          })
          .catch((err) => {
            this.$mainDialog.msgBoxOk(err.message, { noStacking: true });
            this.chdir("");
          })
          .catch((error) => {
            if (error.response !== undefined) {
              if (error.response.status == 500) {
                this.$serverErrorModal(error);
                return;
              }

              return;
            }
          });

        return;
      } else {
        this.$store
          .dispatch("documents/fetchDocuments", {
            searchText,
            recursive: true,
          })
          .catch((error) => {
            if (error.response !== undefined) {
              if (error.response.status == 500) {
                this.$serverErrorModal(error);
                return;
              }
            }

            this.$debugLog(error.message);
          });
      }
    }, 500),

    fieldError(error) {
      let found = this.errors.find((i, e) => {
        return i.field == error.field;
      });

      if (found === undefined) {
        this.errors.push(error);
      } else {
        this.errors.map((e) => {
          if (found.field == e.field) e.error = error.error;
        });
      }
    },

    checkForErrors() {
      return this.errors.find((i, e) => {
        return i.error;
      });
    },

    changeLib(lib) {
      this.changingLib = true;
      this.$store.commit("documents/CLEAN_DOCUMENTS");
      this.currentLib = lib;
      this.chdir("");

      this.$store
        .dispatch("fetchLibraryInfo", lib.uuid)
        .then((data) => {
          this.storage.allowedSpace = data.allowed_storage;
          this.storage.size = data.size;
          this.storage.freeSpace = data.allowed_storage - data.size;

          this.ready = true;
          this.$store
            .dispatch("documents/fetchDocuments", {
              searchText: this.currentDir,
              recursive: true,
            })
            .then(() => {
              try {
                this.$router.replace({ query: { lib: lib.uuid } });
              } catch {
                // do nothing
              }

              this.changingLib = false;
            })
            .catch((error) => {
              this.changingLib = false;
              this.$mainDialog.msgBoxOk(error.message, { noStacking: true });
              this.chdir("");

              if (error.response !== undefined) {
                if (error.response.status == 500) {
                  this.$serverErrorModal(error);
                  return;
                }

                return;
              }
            });
        })
        .catch((error) => {
          this.changingLib = false;
          this.$mainDialog.msgBoxOk(error.message, { noStacking: true });
          this.chdir("");

          if (error.response !== undefined) {
            if (error.response.status == 500) {
              this.$serverErrorModal(error);
              return;
            }

            return;
          }
        });
    },

    createLibraryAction() {
      this.$mediaHttp
        .post("/v1/media/library", this.createLibrary.data)
        .then((resp) => {
          this.$bvToast.toast(this.$t("libraryCreated"), {
            variant: "success",
            title: this.$t("success"),
          });

          this.$store.commit("ADD_LIBRARY", resp.data);

          this.createLibrary.show = false;
          this.createLibrary.data = {};
        })
        .catch((error) => {
          this.$debugLog(error);
          if (error.response !== undefined) {
            if (error.response.status == 500) {
              this.$serverErrorModal(error);
              return;
            }

            this.$mainDialog.msgBoxOk(error.response.data.message);

            return;
          }
        });
    },

    editLib(lib) {
      this.editLibrary.show = true;
      this.editLibrary.data = lib;
    },

    updateLibrary() {
      if (this.checkForErrors()) {
        this.$mainDialog.msgBoxOk(this.$t("someFieldsAreNotValid"));

        return;
      }

      this.$mediaHttp
        .post("/v1/media/library/" + this.currentLib.uuid, this.currentLib)
        .then((resp) => {
          this.$bvToast.toast(this.$t("librarySettingsUpdated"), {
            variant: "success",
            title: "Success",
          });

          this.currentLib = resp.data;
          this.currentLib.roles = this.currentLib.roles.map((r) => {
            return r.role_uuid;
          });
        })
        .catch((error) => {
          if (error.response !== undefined) {
            if (error.response.status == 500) {
              this.$serverErrorModal(error);
              return;
            }

            return;
          }

          this.$mainDialog.msgBoxOk(this.$t("failedToUpdateLibrary"));
        });
    },

    removeLibrary() {
      this.$mainDialog
        .msgBoxConfirm(this.$t("actionWillRemoveLibrary"))
        .then((resp) => {
          if (resp) {
            this.$mediaHttp
              .delete("/v1/media/library/" + this.currentLib.uuid)
              .then(() => {
                this.$bvToast.toast(this.$t("libraryRemoved"), {
                  variant: "success",
                  title: "Success",
                });

                this.libraries = this.libraries.filter((l) => {
                  return l.uuid != this.currentLib.uuid;
                });

                if (this.libraries.length > 0) {
                  this.changeLib(this.libraries[0]);
                } else {
                  this.libraries = null;
                }
              })
              .catch((error) => {
                if (error.response !== undefined) {
                  if (error.response.status == 500) {
                    this.$serverErrorModal(error);
                    return;
                  }

                  return;
                }

                this.$mainDialog.msgBoxOk(this.$t("failedToRemoveLibrary"));
              });
          }
        });
    },

    isProtokolProvider() {
      return this.currentLib.provider == "";
    },

    isLibSelected(lib) {
      if (this.currentLib) {
        return this.currentLib.uuid == lib.uuid;
      }

      return false;
    },

    buy() {
      const popup = this.$loadingPopup();

      this.$transactionApi
        .post("/checkout/link", {
          product_type: "integration",
          reference: "protokol-media",
          lang: this.$store.getters.getLocale,
          source: `platform`,
        })
        .then((resp) => {
          window.open(resp.data, "_blank");
        })
        .catch((err) => {
          this.processInfo = false;

          if (err.response) {
            if (err.response.status == 501) {
              this.$bvToast.toast(this.$t("checkoutMethodCurrentlyDisabled"), {
                title: this.$t("error"),
                variant: "danger",
              });
              return;
            }
          }

          this.$mainDialog.msgBoxOk(this.$t("failedToCheckout"));
        })
        .finally(() => {
          popup.hide();
        });
    },
  },
  mounted() {
    if (!this.$can("use protokol-media")) {
      this.noPermissions = true;
      return;
    }

    this.dirPath = [""];
    if (this.mode == "library") {
      let dirpath = window.location.href.split("#")[1];
      if (dirpath !== undefined) {
        this.dirPath = dirpath.split("/");
      }
    }

    this.$store
      .dispatch("fetchLibraries")
      .then((libs) => {
        let lib = null;
        if (this.mode == "library") {
          lib = this.$route.query.lib;
          if (lib === undefined && libs.length > 0) {
            lib = libs[0].uuid;
          }
        }

        if (this.mode == "standalone") {
          lib = libs[0].uuid;
        }

        this.$store.commit("documents/SET_LIBRARY_UUID", lib);

        if (lib !== undefined && libs.length > 0) {
          this.$store.dispatch("documents/fetchDocuments", {
            searchText: this.currentDir,
            recursive: true,
          });

          this.$store
            .dispatch("fetchLibraryInfo", libs[0].uuid)
            .then((data) => {
              this.storage.allowedSpace = data.allowed_storage;
              this.storage.size = data.size;
              this.storage.freeSPace = data.allowed_storage - data.size;

              this.ready = true;
            })
            .catch((error) => {
              if (error.response !== undefined) {
                if (error.response.status == 500) {
                  this.$serverErrorModal(error);
                  return;
                }

                this.libraryUnavailable = true;
                return;
              }

              if (!error.status) {
                this.libraryUnavailable = true;
              }
            });
        }
      })
      .catch((err) => {
        this.libraryUnavailable = true;
        this.$debugLog(err);
      });

    let checkoutPage = new BroadcastChannel("checkout");
    checkoutPage.onmessage = (msg) => {
      if (msg.data.type == "checkout_finished") {
        this.createLibraryAction();
      }
    };
  },
};
</script>
